import { useState, useEffect, useCallback } from 'react';
import { Search, Check, X, AlertTriangle, Sparkles, Loader2, ChevronRight, FileText } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const SEOAudit = ({ siteId, siteName, onClose, onSiteUpdated, onOpenBlogTab }) => {
  const [step, setStep] = useState('intake'); // intake | scanning | results | fixing | fixed
  const [intake, setIntake] = useState({ site_id: siteId, studio_name: siteName || '', city: '', service_areas: '', work_type: 'Wedding Photography', style_description: '', tagline: '', starting_price: '' });
  const [auditResult, setAuditResult] = useState(null);
  const [fixResult, setFixResult] = useState(null);
  const [scanLines, setScanLines] = useState([]);
  const [error, setError] = useState('');

  // Load existing SEO profile
  useEffect(() => {
    axios.get(`${API}/seo/profile/${siteId}`, { withCredentials: true })
      .then(r => { if (r.data && r.data.city) setIntake(prev => ({ ...prev, ...r.data, site_id: siteId })); })
      .catch(() => {});
  }, [siteId]);

  const addScanLine = useCallback((text, status = 'working') => {
    setScanLines(prev => [...prev, { text, status, id: Date.now() + Math.random() }]);
  }, []);

  const updateLastLine = useCallback((status) => {
    setScanLines(prev => {
      const copy = [...prev];
      if (copy.length > 0) copy[copy.length - 1].status = status;
      return copy;
    });
  }, []);

  const runAudit = async () => {
    if (!intake.city.trim()) { setError('City is required for SEO optimization'); return; }
    setError('');
    setStep('scanning');
    setScanLines([]);

    try {
      const res = await axios.post(`${API}/seo/audit`, intake, { withCredentials: true });
      setAuditResult(res.data);

      // Build all scan lines upfront, then reveal them one by one with staggered delays
      const allLines = [
        { text: `Scanning ${intake.studio_name || siteName}...`, status: 'done' },
        { text: 'Checking page speed...', status: 'done' },
        { text: 'Analyzing mobile responsiveness...', status: 'done' },
        { text: 'Checking SSL certificate...', status: 'done' },
        { text: 'Scanning copy for keywords...', status: 'done' },
        ...res.data.checks.map(c => ({
          text: `${c.label}: ${c.current}`,
          status: c.status === 'pass' ? 'done' : c.status === 'warn' ? 'warn' : 'fail',
        })),
      ];

      // Reveal lines one at a time using setTimeout chain
      allLines.forEach((line, i) => {
        setTimeout(() => {
          setScanLines(prev => [...prev, { ...line, id: Date.now() + i }]);
          // After last line, transition to results
          if (i === allLines.length - 1) {
            setTimeout(() => setStep('results'), 800);
          }
        }, i * 350);
      });

    } catch (err) {
      setError(err.response?.data?.detail || 'Audit failed. Please try again.');
      setStep('intake');
    }
  };

  const runFixes = async () => {
    setStep('fixing');
    setScanLines([]);

    const fixLines = [
      'Rewriting title tag with location keywords...',
      'Crafting meta description...',
      'Adding alt text to all images...',
      `Weaving "${intake.city}" into your copy naturally...`,
      'Replacing placeholder text with your real info...',
      'Adding LocalBusiness schema markup...',
      'Optimizing heading hierarchy...',
    ];

    // Show fix lines one by one
    fixLines.forEach((text, i) => {
      setTimeout(() => {
        setScanLines(prev => [...prev, { text, status: i < fixLines.length - 1 ? 'done' : 'working', id: Date.now() + i }]);
      }, i * 500);
    });

    // Start the actual API call
    try {
      const res = await axios.post(`${API}/seo/fix`, intake, { withCredentials: true, timeout: 120000 });
      // After API returns, mark last line done and add success
      setScanLines(prev => {
        const copy = [...prev];
        if (copy.length > 0) copy[copy.length - 1].status = 'done';
        return [...copy, { text: 'All fixes applied!', status: 'done', id: Date.now() }];
      });
      setFixResult(res.data);
      setTimeout(() => { setStep('fixed'); if (onSiteUpdated) onSiteUpdated(); }, 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fix failed. Please try again.');
      setStep('results');
    }
  };

  const scoreBefore = auditResult?.score_before || 0;
  const scoreAfter = fixResult?.score_after || 0;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="seo-audit-modal" style={{ background: 'var(--bg)', border: '1px solid var(--faint)', maxWidth: '600px', width: '95%', maxHeight: '85vh', overflow: 'auto', padding: '40px', borderRadius: '12px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Search size={18} style={{ color: 'var(--gold)' }} />
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: '22px' }}>Iris SEO</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '18px' }}>&times;</button>
        </div>

        {error && <p style={{ color: '#e88', fontSize: '13px', marginBottom: '16px', padding: '10px', background: 'rgba(196,72,72,0.1)', border: '1px solid rgba(196,72,72,0.2)' }}>{error}</p>}

        {/* INTAKE */}
        {step === 'intake' && (
          <div data-testid="seo-intake">
            <p style={{ color: 'var(--stone)', fontSize: '14px', marginBottom: '24px', lineHeight: 1.7, fontFamily: 'var(--sans)', fontWeight: 300 }}>
              Tell Iris about your business so she can optimize your site for the right keywords and replace placeholder copy with real content.
            </p>
            {[
              { key: 'studio_name', label: 'Studio name', placeholder: 'Love In Color' },
              { key: 'city', label: 'Where are you based? *', placeholder: 'Austin, TX' },
              { key: 'service_areas', label: 'Other areas you serve', placeholder: 'San Antonio, Hill Country, Dallas' },
              { key: 'style_description', label: 'Describe your style', placeholder: 'Cinematic, emotional, lots of natural light' },
              { key: 'tagline', label: 'Tagline or favorite phrase', placeholder: 'We make Tuesdays feel like Saturdays' },
              { key: 'starting_price', label: 'Starting price', placeholder: '$4,500' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: '14px' }}>
                <label style={{ fontFamily: 'var(--mono)', fontSize: '9px', letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--stone)', marginBottom: '6px', display: 'block' }}>{f.label}</label>
                <input
                  value={intake[f.key] || ''}
                  onChange={e => setIntake(prev => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  data-testid={`seo-input-${f.key}`}
                  style={{ width: '100%', padding: '10px 14px', background: 'rgba(242,239,233,0.04)', border: '1px solid var(--faint)', color: 'var(--fg)', fontFamily: 'var(--sans)', fontSize: '14px', outline: 'none' }}
                />
              </div>
            ))}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontFamily: 'var(--mono)', fontSize: '9px', letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--stone)', marginBottom: '6px', display: 'block' }}>What do you mainly shoot?</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {['Wedding Photography', 'Wedding Videography', 'Photo + Video', 'Elopements'].map(t => (
                  <button key={t} onClick={() => setIntake(prev => ({ ...prev, work_type: t }))}
                    style={{ padding: '8px 16px', fontSize: '12px', fontFamily: 'var(--sans)', border: '1px solid', borderColor: intake.work_type === t ? 'var(--gold)' : 'var(--faint)', background: intake.work_type === t ? 'rgba(201,168,76,0.08)' : 'transparent', color: intake.work_type === t ? 'var(--gold)' : 'var(--stone)', cursor: 'pointer' }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={runAudit} className="btn-accent" data-testid="seo-scan-btn" style={{ width: '100%', marginTop: '16px', padding: '14px', fontSize: '12px' }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><Sparkles size={14} /> Scan My Site</span>
            </button>
          </div>
        )}

        {/* SCANNING */}
        {(step === 'scanning' || step === 'fixing') && (
          <div data-testid="seo-scanning">
            <div style={{ fontFamily: 'var(--mono)', fontSize: '12px' }}>
              {scanLines.map((line, idx) => (
                <div key={line.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '6px 0',
                  animation: 'seoLineIn 0.4s ease-out forwards',
                }}>
                  <span style={{ flexShrink: 0, marginTop: '2px' }}>
                    {line.status === 'done' && <Check size={14} style={{ color: '#5ba89d' }} />}
                    {line.status === 'fail' && <X size={14} style={{ color: '#c47272' }} />}
                    {line.status === 'warn' && <AlertTriangle size={14} style={{ color: '#c4a265' }} />}
                    {line.status === 'info' && <Check size={14} style={{ color: 'var(--muted)' }} />}
                    {line.status === 'working' && <Loader2 size={14} className="animate-spin" style={{ color: 'var(--gold)' }} />}
                  </span>
                  <span style={{ color: line.status === 'fail' ? '#c47272' : line.status === 'warn' ? '#c4a265' : 'var(--fg)', lineHeight: 1.5 }}>{line.text}</span>
                </div>
              ))}
            </div>
            <style>{`@keyframes seoLineIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
          </div>
        )}

        {/* RESULTS */}
        {step === 'results' && auditResult && (
          <div data-testid="seo-results">
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <p style={{ fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--stone)', marginBottom: '8px' }}>Your SEO Score</p>
              <p style={{ fontFamily: 'var(--serif)', fontSize: '64px', color: scoreBefore >= 70 ? '#5ba89d' : scoreBefore >= 40 ? 'var(--gold)' : '#c47272' }}>{scoreBefore}</p>
              <div style={{ width: '200px', height: '6px', background: 'var(--faint)', borderRadius: '3px', margin: '8px auto', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${scoreBefore}%`, background: scoreBefore >= 70 ? '#5ba89d' : scoreBefore >= 40 ? 'var(--gold)' : '#c47272', transition: 'width 1s' }} />
              </div>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--stone)', fontWeight: 300 }}>
                {scoreBefore >= 70 ? 'Good — but Iris can do better' : scoreBefore >= 40 ? 'Needs work — Iris can fix this' : 'Needs attention — let Iris handle it'}
              </p>
            </div>
            {auditResult.checks.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 0', borderBottom: '1px solid var(--faint)' }}>
                <span style={{ flexShrink: 0, marginTop: '1px' }}>
                  {c.status === 'pass' ? <Check size={14} style={{ color: '#5ba89d' }} /> : c.status === 'warn' ? <AlertTriangle size={14} style={{ color: '#c4a265' }} /> : c.status === 'info' ? <Check size={14} style={{ color: 'var(--muted)' }} /> : <X size={14} style={{ color: '#c47272' }} />}
                </span>
                <span style={{ flex: 1, fontSize: '13px' }}>{c.label}</span>
                <span style={{ fontSize: '11px', color: 'var(--stone)', maxWidth: '220px', textAlign: 'right', lineHeight: 1.4 }}>{c.current}</span>
              </div>
            ))}
            <button onClick={runFixes} className="btn-accent" data-testid="seo-fix-btn" style={{ width: '100%', marginTop: '24px', padding: '14px', fontSize: '12px' }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><Sparkles size={14} /> Let Iris Fix Everything</span>
            </button>
          </div>
        )}

        {/* FIXED */}
        {step === 'fixed' && fixResult && (
          <div data-testid="seo-fixed">
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <p style={{ fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--stone)', marginBottom: '8px' }}>Your New SEO Score</p>
              <p style={{ fontFamily: 'var(--serif)', fontSize: '64px', color: '#5ba89d' }}>{scoreAfter}</p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '14px', color: 'var(--gold)', fontWeight: 500 }}>
                +{scoreAfter - scoreBefore} points improved
              </p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--stone)', marginTop: '8px', fontWeight: 300 }}>
                On-page SEO is optimized. To reach 100, add blog content targeting local keywords.
              </p>
            </div>
            <button onClick={() => { onClose(); if (onOpenBlogTab) onOpenBlogTab(); }} className="btn-accent" data-testid="seo-open-blog-btn" style={{ width: '100%', padding: '14px', fontSize: '12px' }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><FileText size={14} /> Open Blog Tab (+{100 - scoreAfter} pts to reach 100)</span>
            </button>
            <button onClick={onClose} style={{ width: '100%', marginTop: '10px', padding: '12px', background: 'none', border: '1px solid var(--faint)', color: 'var(--stone)', fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.1em', cursor: 'pointer' }}>
              Done for now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SEOAudit;
